import {createApolloClient} from "../../providers/ApolloClientFactory";
import {organizationStore} from "../../store/organization";
import {Item, TaxType} from "../../types/item";
import {usdFormat} from "../../utils";
import {GET_ITEM_CATEGORIES, GET_ITEMS} from "./queries";
import {recordRename} from "../tokens/registry";
import {Badge} from "../../components/Badge";
import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useNavigation,
    useRevalidator,
    useSearchParams,
} from "react-router-dom";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {eventStore} from "../../store/event";
import moment from "moment";
import {usePermissions} from "../auth/api";
import type {ICellRendererParams} from "ag-grid-community";
import {ColDef, GridApi, SizeColumnsToContentStrategy,} from "ag-grid-community";
import CustomSelect from "../../components/customSelect";
import {PrimaryButton, SecondaryButton} from "../../components/Button";
import {AgGridReact} from "ag-grid-react";
import Loader from "../../components/Loader";
import {userStore} from "../../store/user";
import {UPDATE_ITEM} from "./mutations";
import {GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION} from "../../queries";
import {
    useGridEdit,
    whereByIdNum,
    calculateMenuPriceDisplay,
    calculateStoredPriceFromMenu,
    calculatePriceWithTax,
} from "../helpers/GridEditHelper";
import {menuPriceCentsOf} from "../helpers/pricing";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import GridEditActions from "../helpers/GridEditActions";
import {useGridSave} from "../helpers/UseGridSave";
import {ChevronDownIcon, FunnelIcon} from "@heroicons/react/20/solid";

const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationState} = organizationStore;
const {getState: getEventStore} = eventStore;

const buildSearch = (search: string) => [
    {
        name: {
            _ilike: `%${search}%`,
        },
    },
    {
        vendor: {
            name: {
                _ilike: `%${search}%`,
            },
        },
    },
    {
        category: {
            _ilike: `%${search}%`,
        },
    },
    {
        short_name: {
            _ilike: `%${search}%`,
        },
    },
    {
        description: {
            _ilike: `%${search}%`,
        },
    },
    {
        unique_id: {
            _ilike: `%${search}%`,
        },
    },
];

type ColumnPreset = "all" | "pricing" | "accounting" | "catalog";

// Task-focused column sets so mass grid edits fit on screen. Keys are colId
// (for columns without a field) or field. Id + Item Name stay pinned in every view.
const COLUMN_PRESETS: Record<Exclude<ColumnPreset, "all">, Set<string>> = {
    pricing: new Set(["item_id", "name", "vendor_id", "price", "taxable_item", "tax_percentage", "price_with_tax", "is_active"]),
    accounting: new Set(["item_id", "name", "vendor_id", "event_id", "category", "gl_account_name", "gl_account_number"]),
    catalog: new Set(["item_id", "name", "description", "category", "upc", "redeemable_token_name", "has_image", "vendor_id"]),
};

const COLUMN_PRESET_STORAGE_KEY = "itemsList.columnPreset";

// Below Tailwind's md breakpoint the page switches layouts: the filter cluster
// collapses behind a toggle, the toolbar stacks, and the grid stops pinning /
// fitting columns so they scroll at a readable width instead of being squashed.
const MOBILE_QUERY = "(max-width: 767px)";

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
    useEffect(() => {
        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, []);
    return isMobile;
};

const FilterSegment = ({label, value, options, onChange}: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
}) => (
    <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400 whitespace-nowrap">{label}</span>
        {/* Full-width (and taller, for touch) inside the stacked mobile panel; unchanged on md+. */}
        <div className="flex w-full md:inline-flex md:w-auto rounded-md border border-gray-300 overflow-hidden">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    className={`flex-1 md:flex-none px-2.5 py-1.5 md:py-1 text-xs font-medium whitespace-nowrap border-r border-gray-300 last:border-r-0 ${
                        value === o.value
                            ? "bg-primary-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => onChange(o.value)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    </div>
);

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Item;
}) => JSX.Element | null;
const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: Item }) => string
): CellRenderer => {
    return (params) => {
        if (params.value === undefined) {
            return null;
        }
        return (
            <>
                {permission ? (
                    <Link to={linkFunc(params)}>
                        <Badge>{params.value}</Badge>
                    </Link>
                ) : (
                    <Badge>{params.value}</Badge>
                )}
            </>
        );
    };
};
export const itemsListLoader = async ({request}: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getOrganizationState().organizationId;
    const organizationId = parseInt(
        rawOrganizationId !== null ? rawOrganizationId.toString() : "",
        10
    );
    const eventId = getEventStore().eventId;
    const user = getState().user;

    let where: any = {
        _or: search ? buildSearch(search) : undefined,
    };
    if (organizationId && organizationId !== 0) {
        where = {
            ...where,
            event: {
                organization_id: {
                    _eq: organizationId,
                },
            },
        };
    }
    if (eventId) {
        where = {
            ...where,
            event_id: {
                _eq: eventId,
            },
        };
    } else if (user?.events) {
        where = {
            ...where,
            event_id: {
                _in: user?.events,
            },
        };
    }
    if (user?.vendors) {
        where = {
            ...where,
            vendor_id: {
                _in: user?.vendors,
            },
        };
    }

    let eventWhere: any = {};
    if (organizationId) {
        eventWhere.organization_id = {_eq: organizationId};
        eventWhere.is_active = {_eq: true};
    }
    if (user?.events) eventWhere.id = {_in: user.events};

    let vendorWhere: any = {};
    if (organizationId) {
        vendorWhere.organization_id = {_eq: organizationId};
        vendorWhere.is_active = {_eq: true};
    }
    if (user?.vendors) vendorWhere.id = {_in: user.vendors};

    const itemCategoryWhere = organizationId
        ? {
            organization_id: {_in: [0, organizationId]},
            is_active: {_eq: true},
            reference_type: {_eq: "item_category"},
        }
        : {};

    const [itemsRes, eventRes, vendorsRes, catsRes] = await Promise.all([
        client.query({
            query: GET_ITEMS,
            variables: {where},
            fetchPolicy: "network-only",
        }),
        client.query({
            query: GET_LIST_EVENTS_BY_ORGANIZATION,
            variables: {where: eventWhere},
        }),
        client.query({
            query: GET_LIST_VENDORS_BY_ORGANIZATION,
            variables: {where: vendorWhere},
        }),
        client.query({
            query: GET_ITEM_CATEGORIES,
            variables: {where: itemCategoryWhere},
        }),
    ]);
    return {
        items: itemsRes.data.items,
        events: eventRes.data.events,
        vendors: vendorsRes.data.vendors,
        itemCategories: catsRes.data.dashboard_reference,
    };
};

export const ItemList = () => {
    const {editCreatePermission} = usePermissions("items");
    const canEditItems = !!editCreatePermission;
    const {editCreatePermission: eventsEditPermission} =
        usePermissions("events");
    const {editCreatePermission: vendorsEditPermission} =
        usePermissions("vendors");
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const navigation = useNavigation();
    const [searchParams] = useSearchParams();
    const gridRef = useRef<GridApi>();
    // RP-2566: the menu price typed in the grid, in dollars by item id. Apollo freezes the loader
    // rows, so the parser cannot store it on the row; the formatters, the tax callbacks and the
    // save read it here. Cleared when nothing is pending.
    const typedMenuPriceRef = useRef<Map<number, number>>(new Map());
    const pendingRef = useRef<Record<number, Partial<Item>>>({});
    const shellRef = useRef<HTMLDivElement>(null);
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());
    // The app header wraps to two or three rows on a phone, so the desktop's fixed
    // 148px offset pushed the pagination bar below the fold. Measure the real top
    // instead (scroll-independent, so it survives a resize mid-scroll).
    const [shellHeight, setShellHeight] = useState("calc(100vh - 148px)");
    useEffect(() => {
        const measure = () => {
            const el = shellRef.current;
            if (!el) return;
            const docTop = el.getBoundingClientRect().top + window.scrollY;
            setShellHeight(`${Math.max(360, window.innerHeight - docTop - 16)}px`);
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, []);
    const {items, events, vendors, itemCategories} = useLoaderData() as {
        items: Item[];
        events: { id: number; name: string }[];
        vendors: { id: number; name: string }[];
        itemCategories: Array<{ reference_label: string; reference_value: string; }>;
    };

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    const [rowData, setRowData] = useState<Item[]>([]);
    // Anything that re-runs itemsListLoader: the search box, a Save, or the
    // event/organization switcher in the app header (which revalidates too).
    const isFetching = navigation.state === "loading" || revalidator.state === "loading";
    const [showGridOverlay, setShowGridOverlay] = useState(false);
    useEffect(() => {
        if (!isFetching) {
            setShowGridOverlay(false);
            return;
        }
        // The thin bar covers short fetches; hold the grid overlay back so typing
        // in the search box doesn't strobe it over the rows on every keystroke.
        const t = setTimeout(() => setShowGridOverlay(true), 300);
        return () => clearTimeout(t);
    }, [isFetching]);
    // The box is controlled off the URL so a shared/reloaded ?search= link shows its
    // own term, and both the mobile and desktop copies stay in step with each other.
    const urlSearch = searchParams.get("search") ?? "";
    const [searchInput, setSearchInput] = useState(urlSearch);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const pushedSearchRef = useRef(urlSearch);
    useEffect(() => {
        // Adopt the URL only when it changed from the outside (Back/Forward, a
        // pasted link). Our own debounced push echoes back here a beat later, and
        // adopting that would overwrite whatever was typed while it was in flight.
        if (urlSearch === pushedSearchRef.current) return;
        pushedSearchRef.current = urlSearch;
        setSearchInput(urlSearch);
    }, [urlSearch]);
    useEffect(() => () => clearTimeout(searchTimerRef.current), []);

    const handleSearchChange = useCallback((value: string) => {
        setSearchInput(value);
        clearTimeout(searchTimerRef.current);
        // Each keystroke used to fire its own loader run (four queries, one
        // network-only), so responses could land out of order and settle the grid on
        // a prefix of what was typed. replace: true keeps Back off the keystrokes.
        searchTimerRef.current = setTimeout(() => {
            pushedSearchRef.current = value;
            navigate(
                value.length > 0 ? `/items/list?search=${encodeURIComponent(value)}` : "/items/list",
                {replace: true}
            );
        }, 300);
    }, [navigate]);

    const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");
    const [vendorFilter, setVendorFilter] = useState<number | null>(null);
    const [taxTypeFilter, setTaxTypeFilter] = useState<"all" | "inclusive" | "exclusive">("all");
    const [taxedFilter, setTaxedFilter] = useState<"all" | "taxed" | "untaxed">("all");
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [glFilter, setGlFilter] = useState<"all" | "set" | "missing">("all");
    const [imageFilter, setImageFilter] = useState<"all" | "has" | "missing">("all");
    const [tokenFilter, setTokenFilter] = useState<"all" | "token" | "nontoken">("all");
    const [visibleRowCount, setVisibleRowCount] = useState(0);
    const isMobile = useIsMobile();
    // The six segments are collapsed on phones so the grid keeps the screen; the
    // count on the toggle stands in for the segments while they're out of sight.
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Status defaults to "active", so the grid is pre-filtered on first load with
    // nothing on screen saying so — this keeps the escape hatch visible.
    const activeFilterCount =
        (activeFilter !== "all" ? 1 : 0) +
        (vendorFilter != null ? 1 : 0) +
        (taxTypeFilter !== "all" ? 1 : 0) +
        (taxedFilter !== "all" ? 1 : 0) +
        (categoryFilter != null ? 1 : 0) +
        (glFilter !== "all" ? 1 : 0) +
        (imageFilter !== "all" ? 1 : 0) +
        (tokenFilter !== "all" ? 1 : 0);
    const hasActiveFilters = activeFilterCount > 0 || searchInput.length > 0;

    const clearFilters = useCallback(() => {
        setActiveFilter("all");
        setVendorFilter(null);
        setTaxTypeFilter("all");
        setTaxedFilter("all");
        setCategoryFilter(null);
        setGlFilter("all");
        setImageFilter("all");
        setTokenFilter("all");
        // Skip the debounce: an explicit click should refetch now, not in 300ms.
        clearTimeout(searchTimerRef.current);
        setSearchInput("");
        pushedSearchRef.current = "";
        if (urlSearch) navigate("/items/list", {replace: true});
    }, [navigate, urlSearch]);
    const [columnPreset, setColumnPreset] = useState<ColumnPreset>(() => {
        const saved = window.localStorage.getItem(COLUMN_PRESET_STORAGE_KEY);
        return saved === "pricing" || saved === "accounting" || saved === "catalog" ? saved : "all";
    });
    // Ref so the grid's external-filter callbacks (registered once at grid creation)
    // always see the current values without rebuilding gridOptions.
    const externalFiltersRef = useRef({taxed: taxedFilter, gl: glFilter, image: imageFilter, token: tokenFilter});
    externalFiltersRef.current = {taxed: taxedFilter, gl: glFilter, image: imageFilter, token: tokenFilter};
    // Existing code keyed the active filter off a boolean; keep it derived for those spots.
    const isActiveToggle = activeFilter === "active";
    const {pending, pendingCount, resetBaseline, makeEditable, setPending, getBaselineRows} = useGridEdit<Item>();
    type UPCMode = "json" | "db" | "compare" | "display";
    const getRowId = useCallback((p: { data: Item }) => String(p.data.id), []);

    const gridContext = useMemo(() => ({pending}), [pending]);

    const gridOptions = useMemo(() => ({
        enableCellTextSelection: true,
        context: gridContext,
        suppressKeyboardEvent: (params: any) => {
            const isEditing = params.editing;
            if (isEditing) {
                return true;
            }
            return false;
        },
        // These are external filters (not column filter models) so that NULL/blank
        // values classify predictably (e.g. NULL tax_percentage counts as untaxed).
        isExternalFilterPresent: () => {
            const f = externalFiltersRef.current;
            return f.taxed !== "all" || f.gl !== "all" || f.image !== "all" || f.token !== "all";
        },
        doesExternalFilterPass: (node: any) => {
            const f = externalFiltersRef.current;
            const d = node.data ?? {};
            if (f.taxed !== "all") {
                const pct = Number(d.tax_percentage ?? 0);
                if (f.taxed === "taxed" ? !(pct > 0) : pct > 0) return false;
            }
            if (f.gl !== "all") {
                const has = String(d.gl_account_number ?? "").trim().length > 0
                    || String(d.gl_account_name ?? "").trim().length > 0;
                if (f.gl === "set" ? !has : has) return false;
            }
            if (f.image !== "all") {
                const has = typeof d.image === "string" && d.image.trim().length > 0;
                if (f.image === "has" ? !has : has) return false;
            }
            if (f.token !== "all") {
                const has = String(d.redeemable_token_name ?? "").trim().length > 0 || d.token_price != null;
                if (f.token === "token" ? !has : has) return false;
            }
            return true;
        },
    }), [gridContext]);

    const categoryOptions = useMemo(
        () =>
            Array.from(
                new Set((items ?? [])
                    .map(i => (i.category ?? "").trim())
                    .filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [items]
    );
    const glAccountNameOptions = useMemo(
        () =>
            Array.from(
                new Set((items ?? [])
                    .map(i => (i.gl_account_name ?? "").trim())
                    .filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [items]
    );
    const glAccountNumberOptions = useMemo(
        () =>
            Array.from(
                new Set((items ?? [])
                    .map(i => (i.gl_account_number ?? "").trim())
                    .filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [items]
    );
    const tokenNameOptions = useMemo(
        () =>
            Array.from(
                new Set((items ?? [])
                    .map(i => (i.redeemable_token_name ?? "").trim())
                    .filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [items]
    );

    const eventList = useMemo(() => {
        const base = events
            ? events.map(e => ({id: Number(e.id), name: String(e.name ?? "").trim()}))
            : Array.from(
                new Map(
                    (items ?? [])
                        .map(i => [Number(i.event?.id), String(i.event?.name ?? "")] as [number, string])
                        .filter(([id, name]) => Number.isFinite(id) && name.trim().length > 0)
                ).entries()
            ).map(([id, name]) => ({id, name}));

        return base
            .filter(e => e.name.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [items, events]);

    const eventIdToName = useMemo(
        () => new Map(eventList.map(e => [e.id, e.name])),
        [eventList]
    );

    const eventNameToId = useMemo(
        () => new Map(eventList.map(e => [e.name, e.id])),
        [eventList]
    );

    const vendorList = useMemo(
        () =>
            (vendors ?? [])
                .map(v => ({id: Number(v.id), name: String(v.name ?? "").trim()}))
                .filter(v => v.name.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name)),
        [vendors]
    );

    const vendorIdToName = useMemo(
        () => new Map(vendorList.map(v => [v.id, v.name])),
        [vendorList]
    );

    const vendorNameToId = useMemo(
        () => new Map(vendorList.map(v => [v.name, v.id])),
        [vendorList]
    );

    const categoryList = useMemo(
        () =>
            (itemCategories ?? [])
                .map(c => ({
                    value: String(c.reference_value ?? "").trim(),
                    label: String(c.reference_label ?? "").trim(),
                }))
                .filter(c => c.value.length > 0 && c.label.length > 0)
                .sort((a, b) => a.label.localeCompare(b.label)),
        [itemCategories]
    );

    const catValueToLabel = useMemo(
        () => new Map(categoryList.map(c => [c.value, c.label] as const)),
        [categoryList]
    );

    const catLabelToValue = useMemo(
        () => new Map(categoryList.map(c => [c.label, c.value] as const)),
        [categoryList]
    );

    const normalizeUPC = (raw: unknown, mode: UPCMode = "json"): string => {
        const s = (raw ?? "").toString().trim();
        const emptyOut = (m: UPCMode) => (m === "db" || m === "compare" || m === "display" ? "" : '[""]');

        if (!s || s === "[]" || s === '[""]') return emptyOut(mode);

        let arr: string[] | null = null;

        try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) {
                arr = parsed.map(String).map(t => t.trim()).filter(Boolean);
            }
        } catch {
        }

        if (!arr) {
            arr = s
                .split(/[,\s]+/)
                .map(t => t.replace(/^"+|"+$/g, "").trim())
                .filter(Boolean);
        }

        if (!arr.length) return emptyOut(mode);
        if (mode === "display") return arr.join(", ");
        if (mode === "compare") return JSON.stringify(arr);
        return JSON.stringify(arr);
    };

    useEffect(() => {
        // A refetch — search, save, or the event/organization switch in the app
        // header — used to wipe uncommitted edits with no warning. Rebase the
        // baseline onto the fresh rows but keep the patches; getPatchedValue
        // re-overlays them at render, and Save/Discard still clear them.
        resetBaseline(items, {keepPending: true});
        setRowData(items);
    }, [items, resetBaseline]);

    useEffect(() => {
        pendingRef.current = pending;
        // RP-2566: nothing pending means saved, discarded, or edited back to the original
        if (Object.keys(pending).length === 0) typedMenuPriceRef.current.clear();
        gridRef.current?.refreshCells({ force: true });
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const model: Record<string, any> = {};
        if (activeFilter !== "all") {
            model.is_active = { filterType: 'text', type: 'equals', filter: activeFilter === "active" ? 'Yes' : 'No' };
        }
        // The Vendor column's valueGetter yields the vendor name, so filter by name.
        const vendorName = vendorFilter != null ? vendorIdToName.get(vendorFilter) : undefined;
        if (vendorName) {
            model.vendor_id = { filterType: 'text', type: 'equals', filter: vendorName };
        }
        // The Tax Type column's filterValueGetter yields "Inclusive"/"Exclusive".
        if (taxTypeFilter !== "all") {
            model.taxable_item = { filterType: 'text', type: 'equals', filter: taxTypeFilter === "inclusive" ? 'Inclusive' : 'Exclusive' };
        }
        // The Category column's filterValueGetter yields the category label.
        if (categoryFilter) {
            model.category = { filterType: 'text', type: 'equals', filter: categoryFilter };
        }
        api.setFilterModel(Object.keys(model).length ? model : null);
        // Also re-runs the external filters (taxed/GL/image/token) via their refs.
        api.onFilterChanged();
    }, [activeFilter, pendingCount, vendorFilter, vendorIdToName, taxTypeFilter, taxedFilter,
        categoryFilter, glFilter, imageFilter, tokenFilter]);

    useEffect(() => {
        window.localStorage.setItem(COLUMN_PRESET_STORAGE_KEY, columnPreset);
        const api = gridRef.current;
        if (!api) return;
        // Defer until the new column visibility has rendered, then size for the mode:
        // presets fit the viewport; "all" auto-sizes to content and scrolls.
        // On a phone even a preset's 7-8 columns would be squeezed to ~45px each,
        // so always size to content there and let the grid scroll sideways.
        const t = setTimeout(() => {
            if (columnPreset === "all" || isMobile) api.autoSizeAllColumns();
            else api.sizeColumnsToFit();
        }, 0);
        return () => clearTimeout(t);
    }, [columnPreset, isMobile]);

    useEffect(() => {
        originalActiveRef.current = new Map(
            (items ?? []).map(i => [String(i.id), !!i.is_active])
        );
    }, [items]);

    const generateColDefs = (): ColDef<Item>[] => [
        {
            headerName: "Id",
            colId: "item_id",
            valueGetter: (p) => p.data?.id,
            cellRenderer: createCellRenderer(
                editCreatePermission,
                (p) => `/items/${p.data.id}/edit`
            ),
            filter: "agTextColumnFilter",
            // Two pinned columns leave almost no scrollable width on a phone.
            pinned: isMobile ? null : "left",
        },
        {
            headerName: "Item Name",
            field: "name",
            editable: canEditItems,
            ...makeEditable("name", {type: "text", canEdit: canEditItems}, setRowData),
            filter: "agTextColumnFilter",
            pinned: isMobile ? null : "left",
        },
        {
            headerName: "Product Description",
            field: "description",
            editable: canEditItems,
            ...makeEditable("description", {type: "text", canEdit: canEditItems}, setRowData),
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Is Active?",
            field: "is_active",
            cellDataType: "text",
            sortable: true,
            filter: "agTextColumnFilter",
            ...makeEditable("is_active", { type: "boolean", canEdit: canEditItems, toggleOnClick: true }, setRowData),

            valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),

            filterValueGetter: (p: any) => {
                const id = String(p.data?.id ?? "");
                const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;

                if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                    return patch.is_active ? "Yes" : "No";
                }

                const baseline = originalActiveRef.current.get(id);
                return baseline ? "Yes" : "No";
            },
        },
        {
            headerName: "Tax Type",
            field: "taxable_item",
            cellDataType: "text",
            ...makeEditable(
                "taxable_item",
                {
                    type: "boolean",
                    canEdit: canEditItems,
                    labels: {on: "Exclusive", off: "Inclusive"},
                    toggleOnClick: true,
                    onAfterChange: ({ newValue, row, updateRow }) => {
                        const newIsExclusive = Boolean(newValue);

                        const oldIsExclusive = !newIsExclusive;
                        const oldTaxPct = Number(row.tax_percentage ?? 0);

                        const typed = typedMenuPriceRef.current.get(row.id);
                        const menu =
                            typeof typed === "number"
                                ? typed
                                : calculateMenuPriceDisplay(Number(row.price ?? 0), oldTaxPct, oldIsExclusive, row.menu_price);

                        const newStored = calculateStoredPriceFromMenu(menu, oldTaxPct, newIsExclusive);
                        updateRow({ price: newStored }, true);
                    },
                },
                setRowData
            ),
            sortable: true,
            filter: "agTextColumnFilter",
            filterValueGetter: (p) => (getPatchedValue(p, "taxable_item") ? "Exclusive" : "Inclusive"),
        },
        {
            headerName: "Menu Price",
            field: "price",
            ...makeEditable(
                "price",
                {
                    type: "number",
                    canEdit: canEditItems,
                    format: (storedPriceCents: number, row: any) => {
                        const taxPercentage = Number(row?.tax_percentage ?? 0);
                        const isExclusive = Boolean(row?.taxable_item);
                        // RP-1955: what was typed wins; a floored base cannot be turned back into it exactly
                        const typedId = row?.id;
                        const typed = typedId != null ? typedMenuPriceRef.current.get(typedId) : undefined;
                        const menuPrice = typeof typed === "number"
                            ? typed
                            : calculateMenuPriceDisplay(storedPriceCents, taxPercentage, isExclusive, row?.menu_price);
                        return usdFormat.format(menuPrice);
                    },
                    parser: (newValue: any, oldValue: any, row: any) => {
                        const menuPriceDollars = Number(String(newValue ?? "").replace(/[^0-9.-]/g, ""));
                        if (!Number.isFinite(menuPriceDollars)) {
                            return typeof oldValue === "number" ? oldValue : 0;
                        }
                        const taxPercentage = Number(row?.tax_percentage ?? 0);
                        const isExclusive = Boolean(row?.taxable_item);
                        // RP-2566: record what was typed off the row. The loader rows are frozen by Apollo,
                        // and a write onto them throws, which left the cell stuck in edit mode.
                        if (row?.id != null) {
                            typedMenuPriceRef.current.set(row.id, menuPriceDollars);
                        }
                        return calculateStoredPriceFromMenu(menuPriceDollars, taxPercentage, isExclusive);
                    },
                    onAfterChange: ({ newValue, row, updateRow }) => {
                        const taxPercentage = Number(row?.tax_percentage ?? 0);
                        const isExclusive = Boolean(row?.taxable_item);
                        let typed = typedMenuPriceRef.current.get(row.id);
                        if (typeof typed !== "number") {
                            // the value did not come through the parser; derive it from the new base
                            typed = calculateMenuPriceDisplay(Number(newValue ?? 0), taxPercentage, isExclusive, row?.menu_price);
                            typedMenuPriceRef.current.set(row.id, typed);
                        }
                        // RP-2566: a typed price can floor to the stored base ($15.00 at 19.375% keeps 1256),
                        // so `price` alone never reaches pending. Track menu_price too, so Save appears and
                        // mutateRow writes it; back at the original, drop it again.
                        const typedCents = Math.round(typed * 100);
                        const baselineRow = getBaselineRows().find(r => r.id === row.id) ?? row;
                        const unchanged = typedCents === menuPriceCentsOf(baselineRow);
                        updateRow({ menu_price: typedCents }, true /* silent: pending is handled below */);
                        setPending(prev => {
                            const copy = { ...prev };
                            const patch: any = { ...(copy[row.id] || {}) };
                            if (unchanged) delete patch.menu_price; else patch.menu_price = typedCents;
                            if (Object.keys(patch).length) copy[row.id] = patch; else delete copy[row.id];
                            return copy;
                        });
                    }
                },
                setRowData
            ),
            // RP-2566: makeEditable keys the highlight on `price`; a menu_price-only edit is dirty too
            cellClassRules: {
                "cell-dirty": (p) => {
                    const id = p.data?.id;
                    const patch = id != null ? pendingRef.current[id] : undefined;
                    return !!patch && ("price" in patch || "menu_price" in patch);
                },
            },
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Tax Percentage",
            field: "tax_percentage",
            cellDataType: "number",
            ...makeEditable(
                "tax_percentage",
                {
                    type: "number",
                    canEdit: canEditItems,
                    precision: 6,
                    onAfterChange: ({newValue, row, updateRow}) => {
                        const newTaxPct = Number(newValue);
                        const isExclusive = Boolean(row.taxable_item);

                        const oldTaxPct = Number(row.tax_percentage ?? 0);

                        const typed = typedMenuPriceRef.current.get(row.id);
                        const menu =
                            typeof typed === "number"
                                ? typed
                                : calculateMenuPriceDisplay(Number(row.price ?? 0), oldTaxPct, isExclusive, row.menu_price);

                        const newStored = calculateStoredPriceFromMenu(menu, newTaxPct, isExclusive);
                        updateRow({price: newStored}, true);
                    },
                },
                setRowData
            ),
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Price with Tax",
            colId: "price_with_tax",
            filter: "agTextColumnFilter",
            valueGetter: (p) => {
                const storedPrice = Number(p?.data?.price ?? 0);
                const taxPercentage = Number(p?.data?.tax_percentage ?? 0);
                const isExclusive = Boolean(p?.data?.taxable_item);
                // RP-1955: an inclusive item is charged at its menu price
                const typedId = p?.data?.id;
                const typed = typedId != null ? typedMenuPriceRef.current.get(typedId) : undefined;
                if (!isExclusive && typeof typed === "number") return typed;
                return calculatePriceWithTax(storedPrice, taxPercentage, isExclusive, p?.data?.menu_price);
            },
            valueFormatter: (params) => usdFormat.format(params.value ?? 0),
        },
        {
            headerName: "Category",
            field: "category",
            cellEditor: TextSuggestEditor,
            cellEditorParams: (p: { value: any; data: Item }) => ({
                values: categoryList.map(c => c.label),
                showAllOnEdit: true,
                initialValue:
                    catValueToLabel.get(String(p.value ?? p.data?.category ?? "").trim()) ??
                    String(p.value ?? p.data?.category ?? ""),
            }),
            ...makeEditable(
                "category",
                {
                    type: "text",
                    canEdit: canEditItems,
                    parser: (nv: any, ov: any) => {
                        const s = String(nv ?? "").trim();
                        if (!s) return ov;
                        const byLabel = catLabelToValue.get(s);
                        if (byLabel) return byLabel;
                        return s;
                    },
                    formatter: (v: any) =>
                        catValueToLabel.get(String(v ?? "").trim()) ?? String(v ?? "").trim(),
                    equals: (a: any, b: any) =>
                        String(a ?? "").trim() === String(b ?? "").trim(),
                },
                setRowData
            ),

            filter: "agTextColumnFilter",
            filterValueGetter: (p) =>
                catValueToLabel.get(String((p.data as Item).category ?? "").trim()) ??
                String((p.data as Item).category ?? "").trim(),
        },
        {
            headerName: "UPC",
            field: "upc",
            editable: canEditItems,
            ...makeEditable(
                "upc",
                {
                    type: "text",
                    canEdit: canEditItems,
                    formatter: (v: any) => normalizeUPC(v, "json"),
                    parser: (nv: any, ov: any) => normalizeUPC(nv, "json"),
                    equals: (a: any, b: any) => normalizeUPC(a, "compare") === normalizeUPC(b, "compare"),
                },
                setRowData
            ),
            cellRenderer: (p: ICellRendererParams<Item, unknown>) => normalizeUPC(p.value, "display"),
            cellEditor: "agTextCellEditor",
            cellEditorParams: {useFormatter: true},
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Token Name",
            field: "redeemable_token_name",
            cellEditor: TextSuggestEditor,
            cellEditorParams: {
                values: tokenNameOptions,
                showAllOnEdit: true,
            },
            ...makeEditable(
                "redeemable_token_name",
                {
                    type: "text",
                    canEdit: canEditItems,
                    formatter: (v: any) => String(v ?? "").trim(),
                    parser: (nv: any) => String(nv ?? "").trim(),
                    equals: (a: any, b: any) => String(a ?? "").trim() === String(b ?? "").trim(),
                },
                setRowData
            ),
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Has Image",
            colId: "has_image",
            valueGetter: (p) =>
                typeof p.data?.image === "string" && p.data.image.trim().length > 0,
            cellRenderer: (p: { value: any; }) => p.value ? "Yes" : "No",
            valueFormatter: (p) => (p.value ? "Yes" : "No"),
            filter: "agTextColumnFilter",
            sortable: true,
        },
        {
            headerName: "Event",
            field: "event_id",
            cellEditor: TextSuggestEditor,
            cellEditorParams: (p: { value: any; data: Item }) => ({
                values: eventList.map(e => e.name),
                showAllOnEdit: true,
                initialValue:
                    eventIdToName.get(
                        Number(p.value ?? p.data?.event_id ?? (p as any)?.data?.event?.id)
                    ) ?? (p as any)?.data?.event?.name ?? "",
            }),
            ...makeEditable(
                "event_id",
                {
                    type: "text",
                    canEdit: canEditItems,
                    parser: (nv: any, ov: any) => {
                        const s = String(nv ?? "").trim();
                        if (!s) return ov;
                        const byName = eventNameToId.get(s);
                        if (byName != null) return byName;
                        const n = Number(s);
                        return (!Number.isNaN(n) && eventIdToName.has(n)) ? n : ov;
                    },
                    equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                },
                setRowData
            ),
            cellRenderer: (p: { value: any; data: Item }) => {
                const id = Number(p.value ?? p.data?.event_id ?? (p as any)?.data?.event?.id);
                const name = eventIdToName.get(id) ?? (p as any)?.data?.event?.name ?? "";
                if (!name) return null;

                return eventsEditPermission ? (
                    <Link
                        to={`/events/${id}/edit`}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Badge>{name}</Badge>
                    </Link>
                ) : (
                    <Badge>{name}</Badge>
                );
            },
            filter: "agTextColumnFilter",
            valueGetter: (p) => {
                const id = Number(
                    (p.data as Item).event_id ?? (p.data as any)?.event?.id
                );
                return eventIdToName.get(id) ?? (p.data as any)?.event?.name ?? "";
            },
        },
        {
            headerName: "Vendor",
            field: "vendor_id",
            cellEditor: TextSuggestEditor,
            cellEditorParams: (p: { value: any; }) => ({
                values: vendorList.map(v => v.name),
                showAllOnEdit: true,
                initialValue: vendorIdToName.get(Number(p.value)) ?? "",
            }),
            ...makeEditable(
                "vendor_id",
                {
                    type: "text",
                    canEdit: canEditItems,
                    parser: (nv: any, ov: any) => {
                        const s = String(nv ?? "").trim();
                        if (!s) return ov;
                        const byName = vendorNameToId.get(s);
                        if (byName != null) return byName;
                        const n = Number(s);
                        return (!Number.isNaN(n) && vendorIdToName.has(n)) ? n : ov;
                    },
                    equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                },
                setRowData
            ),
            cellRenderer: (p: { value: any; data: { vendor_id: any; vendor: { id: any; name: any; }; }; }) => {
                const id = Number(p.value ?? p.data?.vendor_id ?? p.data?.vendor?.id);
                const name = vendorIdToName.get(id) ?? p.data?.vendor?.name ?? "";
                if (!name) return null;

                return vendorsEditPermission ? (
                    <Link
                        to={`/vendors/${id}/edit`}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Badge>{name}</Badge>
                    </Link>
                ) : (
                    <Badge>{name}</Badge>
                );
            },
            filter: "agTextColumnFilter",
            valueGetter: (p) => {
                const id = Number((p.data as Item).vendor_id ?? (p.data as any)?.vendor?.id);
                return vendorIdToName.get(id) ?? (p.data as any)?.vendor?.name ?? "";
            },
        },
        {
            headerName: "GL Account Name",
            field: "gl_account_name",
            cellEditor: TextSuggestEditor,
            cellEditorParams: {
                values: glAccountNameOptions,
                showAllOnEdit: true,
            },
            ...makeEditable("gl_account_name", {type: "text", canEdit: canEditItems}, setRowData),
            filter: "agTextColumnFilter",
        },
        {
            headerName: "GL Account Number",
            field: "gl_account_number",
            cellEditor: TextSuggestEditor,
            cellEditorParams: {
                values: glAccountNumberOptions,
                showAllOnEdit: true,
            },
            ...makeEditable("gl_account_number", {type: "text", canEdit: canEditItems}, setRowData),
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Updated By",
            colId: "updated_by",
            valueGetter: (p) => p.data?.user?.username,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Created Date",
            colId: "created_date",
            valueGetter: (p) => p.data?.created_at,
            valueFormatter: (p) => {
                const date = p.value ? moment(p.value) : null;
                return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
            },
            filter: "agTextColumnFilter",
            filterValueGetter: (p) =>
                p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
        },
        {
            headerName: "Updated Date",
            colId: "updated_date",
            valueGetter: (p) => p.data?.updated_at,
            valueFormatter: (p) => {
                const date = p.value ? moment(p.value) : null;
                return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
            },
            filter: "agTextColumnFilter",
            filterValueGetter: (p) =>
                p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
        },
    ];

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
        // RP-2583: columns stay put. Dragging a header (easy to do by accident on a phone)
        // used to reorder columns or pull them off the grid.
        suppressMovable: true,
    }), []);

    const colDefs: ColDef<Item>[] = useMemo(
        () => {
            const defs = generateColDefs();
            // hide is a stateful ag-grid attribute: omitting it keeps each column's
            // current visibility, so "all" must explicitly un-hide every column.
            if (columnPreset === "all") return defs.map((d) => ({...d, hide: false}));
            const visible = COLUMN_PRESETS[columnPreset];
            return defs.map((d) => ({
                ...d,
                hide: !visible.has(String((d as any).colId ?? d.field ?? "")),
            }));
        },
        [
            makeEditable,
            setRowData,
            editCreatePermission,
            eventsEditPermission,
            vendorsEditPermission,
            categoryOptions,
            tokenNameOptions,
            glAccountNameOptions,
            glAccountNumberOptions,
            eventIdToName, eventNameToId,
            vendorIdToName, vendorNameToId,
            isActiveToggle,
            columnPreset,
            isMobile,
        ]
    );

    const rowHeight = isMobile ? 40 : 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };
    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            const params = {
                fileName: `Items_${timestamp}.csv`,
                columnSeparator: ",",
            };
            gridRef.current.exportDataAsCsv(params);
        }
    };

    const mutateRow = async (id: number, patch: Partial<Item>) => {
        const where = whereByIdNum(String(id));
        const user = getState().user;

        const baselineMap = new Map(getBaselineRows().map(r => [r.id, r]));
        const baselineRow = baselineMap.get(id)!;

        // RP-2566: the menu price typed in the grid lives in typedMenuPriceRef, not on the row
        const typedMenu: number | undefined = typedMenuPriceRef.current.get(id);

        const after = { ...baselineRow, ...patch };

        const taxChanged =
            Object.prototype.hasOwnProperty.call(patch, "taxable_item") ||
            Object.prototype.hasOwnProperty.call(patch, "tax_percentage");
        const priceEdited =
            Object.prototype.hasOwnProperty.call(patch, "price") ||
            Object.prototype.hasOwnProperty.call(patch, "menu_price");

        let finalPriceCents: number | undefined;
        let finalMenuPriceCents: number | undefined;

        if (taxChanged || priceEdited) {
            const afterTaxPct = Number(after.tax_percentage ?? 0);
            const afterExclusive = Boolean(after.taxable_item);

            const menuDollars =
                typeof typedMenu === "number"
                    ? typedMenu
                    : calculateMenuPriceDisplay(
                        Number(baselineRow.price ?? 0),
                        Number(baselineRow.tax_percentage ?? 0),
                        Boolean(baselineRow.taxable_item),
                        baselineRow.menu_price
                    );

            const safe = (p: number) => (p >= 1 ? 0.999999 : p);

            finalPriceCents = calculateStoredPriceFromMenu(
                menuDollars,
                safe(afterTaxPct),
                afterExclusive
            );
            // RP-1955: the typed menu price is stored next to the base; the tablets charge it as typed
            finalMenuPriceCents = Math.round(menuDollars * 100);

            setRowData(prev =>
                prev.map(r => (r.id === id ? { ...r, price: finalPriceCents!, menu_price: finalMenuPriceCents! } : r))
            );

            setPending(prev => {
                const copy = { ...prev };
                if (copy[id] && ("price" in copy[id]! || "menu_price" in copy[id]!)) {
                    delete (copy[id] as any).price;
                    delete (copy[id] as any).menu_price;
                    if (!Object.keys(copy[id]!).length) delete copy[id];
                }
                return copy;
            });
        }

        const input: any = {
            ...patch,
            ...(finalPriceCents != null ? { price: finalPriceCents, menu_price: finalMenuPriceCents } : null),
            last_updated_by: user?.id,
            updated_at: new Date().toISOString(),
        };

        if ("redeemable_token_name" in patch) {
            input.redeemable_token_id = patch.redeemable_token_name;
        }
        if ("taxable_item" in patch) {
            input.tax_type = patch.taxable_item ? TaxType.EXCLUSIVE : TaxType.INCLUSIVE;
        }

        await client.mutate({
            mutation: UPDATE_ITEM,
            variables: { input, where },
            errorPolicy: "all",
         });

        // Token rename fix: inline grid edits of the token name are renames
        // too — record old->new in the registry (best-effort).
        if ("redeemable_token_name" in patch && patch.redeemable_token_name) {
            const oldTokenName =
                (baselineRow as any)?.redeemable_token_name ??
                (baselineRow as any)?.redeemable_token_id ??
                "";
            await recordRename(
                client,
                Number((baselineRow as any)?.event_id),
                oldTokenName,
                String(patch.redeemable_token_name)
            );
        }
    };

    const { handleSave } = useGridSave<Item>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });
    return (
        <div
            ref={shellRef}
            className="w-full flex flex-col overflow-hidden"
            style={{height: shellHeight}}
            aria-busy={isFetching}
        >
            <div className="relative bg-white rounded-md shadow-sm px-3 md:px-4 pt-1 pb-3 mb-2 mt-3">
            {isFetching && (
                <div
                    className="absolute inset-x-0 top-0 h-[3px] overflow-hidden rounded-t-md bg-primary-100"
                    aria-hidden="true"
                >
                    <div className="ronin-loading-bar h-full w-1/5 bg-primary-600"/>
                </div>
            )}
            {/* Phones: one control per row band instead of a wrapping flex line —
                the two fixed-width selects used to spill onto three ragged rows. */}
            <div className="md:hidden flex flex-col gap-2 min-w-0 py-2">
                <input
                    name="search"
                    placeholder="Search Items"
                    className="block w-full h-[38px] rounded-md border-gray-300 shadow-sm focus:border-red-500 text-sm"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />

                <div className="flex gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                        <CustomSelect
                            placeholderOnly="Vendor"
                            options={vendorList.map(v => ({value: v.id, label: v.name}))}
                            value={vendorFilter}
                            isClearable
                            onChange={(v: number | null) => setVendorFilter(v ?? null)}
                        />
                    </div>

                    <div className="flex-1 min-w-0">
                        <CustomSelect
                            placeholderOnly="Category"
                            options={categoryList.map(c => ({value: c.label, label: c.label}))}
                            value={categoryFilter}
                            isClearable
                            onChange={(v: string | null) => setCategoryFilter(v ?? null)}
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-y-2">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={items}
                        getBaselineRows={getBaselineRows}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                    <SecondaryButton onClick={handleExportCSV}>Export CSV</SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/items/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-3 min-w-0">
                    <input
                        name="search"
                        placeholder="Search Items"
                        className="block w-full h-[38px] rounded-md border-gray-300 shadow-sm focus:border-red-500 sm:text-sm"
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </div>

                <div className="col-span-2 min-w-0">
                    <CustomSelect
                        placeholderOnly="Vendor"
                        options={vendorList.map(v => ({value: v.id, label: v.name}))}
                        value={vendorFilter}
                        isClearable
                        onChange={(v: number | null) => setVendorFilter(v ?? null)}
                    />
                </div>

                <div className="col-span-2 min-w-0">
                    <CustomSelect
                        placeholderOnly="Category"
                        options={categoryList.map(c => ({value: c.label, label: c.label}))}
                        value={categoryFilter}
                        isClearable
                        onChange={(v: string | null) => setCategoryFilter(v ?? null)}
                    />
                </div>

                <div className="col-span-2 col-start-8 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={items}
                        getBaselineRows={getBaselineRows}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/items/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="bg-gray-50 rounded-md px-3 py-2">
            {/* Phones: six segments stacked would swallow the grid, so they hide
                behind this row. The badge keeps the count visible while collapsed. */}
            <div className="flex md:hidden items-center gap-2">
                <button
                    type="button"
                    onClick={() => setFiltersOpen((o) => !o)}
                    aria-expanded={filtersOpen}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700"
                >
                    <FunnelIcon className="h-4 w-4 text-gray-400" aria-hidden="true"/>
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                            {activeFilterCount}
                        </span>
                    )}
                    <ChevronDownIcon
                        className={`h-4 w-4 text-gray-400 transition-transform ${filtersOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    />
                </button>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                    <span className="font-medium text-gray-700">{visibleRowCount.toLocaleString()}</span>
                    {" of "}
                    <span className="font-medium text-gray-700">{rowData.length.toLocaleString()}</span>
                </span>
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="ml-auto shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700"
                    >
                        Clear
                    </button>
                )}
            </div>

            <div
                className={`${filtersOpen ? "grid" : "hidden"} grid-cols-2 gap-3 pt-3 md:pt-0 md:flex md:flex-wrap md:items-end md:gap-x-5 md:gap-y-2`}
            >
                <FilterSegment
                    label="Status"
                    value={activeFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "active", label: "Active"},
                        {value: "inactive", label: "Inactive"},
                    ]}
                    onChange={(v) => setActiveFilter(v as typeof activeFilter)}
                />
                <FilterSegment
                    label="Tax Type"
                    value={taxTypeFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "inclusive", label: "Inclusive"},
                        {value: "exclusive", label: "Exclusive"},
                    ]}
                    onChange={(v) => setTaxTypeFilter(v as typeof taxTypeFilter)}
                />
                <FilterSegment
                    label="Tax %"
                    value={taxedFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "taxed", label: "> 0"},
                        {value: "untaxed", label: "0"},
                    ]}
                    onChange={(v) => setTaxedFilter(v as typeof taxedFilter)}
                />
                <FilterSegment
                    label="GL"
                    value={glFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "set", label: "Set"},
                        {value: "missing", label: "Missing"},
                    ]}
                    onChange={(v) => setGlFilter(v as typeof glFilter)}
                />
                <FilterSegment
                    label="Image"
                    value={imageFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "has", label: "Has"},
                        {value: "missing", label: "Missing"},
                    ]}
                    onChange={(v) => setImageFilter(v as typeof imageFilter)}
                />
                <FilterSegment
                    label="Tokens"
                    value={tokenFilter}
                    options={[
                        {value: "all", label: "All"},
                        {value: "token", label: "Token"},
                        {value: "nontoken", label: "Non-token"},
                    ]}
                    onChange={(v) => setTokenFilter(v as typeof tokenFilter)}
                />
                <div className="hidden md:flex items-center gap-2 pb-1">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                        Showing{" "}
                        <span className="font-medium text-gray-700">{visibleRowCount.toLocaleString()}</span>
                        {" of "}
                        <span className="font-medium text-gray-700">{rowData.length.toLocaleString()}</span> items
                    </span>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                <div className="col-span-2 border-t border-gray-200 pt-3 md:border-t-0 md:pt-0 md:ml-auto md:pl-5 md:border-l">
                    <FilterSegment
                        label="Columns"
                        value={columnPreset}
                        options={[
                            {value: "all", label: "All"},
                            {value: "pricing", label: "Pricing"},
                            {value: "accounting", label: "Accounting"},
                            {value: "catalog", label: "Catalog"},
                        ]}
                        onChange={(v) => setColumnPreset(v as ColumnPreset)}
                    />
                </div>
            </div>
            </div>
            </div>

            <div className="flex-1 min-h-0 relative">
                {showGridOverlay && (
                    <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60">
                        <div className="mt-24 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-md">
                            <Loader sm/>
                            <span className="text-sm font-medium text-gray-700">Loading items…</span>
                        </div>
                    </div>
                )}
                <div
                    className="ag-theme-quartz w-full h-full min-w-0"
                    style={isMobile ? ({"--ag-font-size": "12px"} as React.CSSProperties) : undefined}
                >
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        suppressDragLeaveHidesColumns
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onModelUpdated={(e) => setVisibleRowCount(e.api.getDisplayedRowCount())}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            const model: Record<string, any> = {};
                            if (activeFilter !== "all") {
                                model.is_active = { filterType: 'text', type: 'equals', filter: activeFilter === "active" ? 'Yes' : 'No' };
                            }
                            const vendorName = vendorFilter != null ? vendorIdToName.get(vendorFilter) : undefined;
                            if (vendorName) {
                                model.vendor_id = { filterType: 'text', type: 'equals', filter: vendorName };
                            }
                            if (taxTypeFilter !== "all") {
                                model.taxable_item = { filterType: 'text', type: 'equals', filter: taxTypeFilter === "inclusive" ? 'Inclusive' : 'Exclusive' };
                            }
                            if (categoryFilter) {
                                model.category = { filterType: 'text', type: 'equals', filter: categoryFilter };
                            }
                            if (Object.keys(model).length) {
                                params.api.setFilterModel(model);
                            }
                            params.api.onFilterChanged();
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
